import * as com from "../../Common"

export async function fetchSelectedProductV2(loanId, token) {
  const response = await fetch(`/los/v2/loans/${loanId}/selected-product`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + token,
      Cache: "no-cache",
    },
  })
  if (response.status !== 200) {
    throw new Error(`selected-product request failed: ${response.status}`)
  }
  const js = await response.json()
  if (js.Status !== "OK") {
    throw new Error(js.Text || "selected-product request failed")
  }
  return js
}

export function loanIdFromPath() {
  const paths = window.location.pathname.split("/")
  return paths[paths.length - 1] || ""
}

export function purposeLabel(purpose) {
  switch (purpose) {
    case "purchase":
      return "Purchase"
    case "refinance":
    case "ratetermrefinance":
      return "Refinance to improve rate or term"
    case "cashoutrefinance":
      return "Cash out refinance"
    default:
      return "Unknown"
  }
}

export function loanTypeLabel(product) {
  if (!product) return "-"
  if (product.amortization === "fixed" || !product.arm) return "Fixed rate"
  const fixed = product.arm.fixedPeriod ?? 0
  const adjust = product.arm.adjustmentPeriod ?? 1
  return `ARM ${fixed}/${adjust}`
}

export function refinanceProgramLabel(program) {
  switch (program) {
    case "fixed":
      return "Fixed rate"
    case "arm":
      return "ARM"
    case "baloon":
      return "Baloon"
    case "heloc":
      return "HELOC"
    case "negativeamortization":
      return "Negative Amortization"
    case "interestonly":
      return "Interest Only"
    case "noloan":
      return "No current loan"
    default:
      return "No current loan specified"
  }
}

export function subjectPropertyLabel(property) {
  if (property?.purpose === "purchase" && property?.hasContract === false) {
    return "TBD"
  }
  return property?.address || "-"
}

export function maxLoanSizeLabel(property) {
  const county = (property?.county || "").toUpperCase()
  if (!county || com.counties[county] === undefined) {
    return "Unknown for " + (property?.county || "county")
  }
  const units = parseInt(property?.units, 10) || 1
  return com.counties[county][units - 1]
}

export function closingCostDisplay(closingCost) {
  let cc = Number(closingCost)
  if (Number.isNaN(cc) || cc < 0) cc = 0
  return Math.trunc(cc)
}

/** Purchase uses sales price; refinance uses appraisal — same as Fee Sheet Overview. */
export function estimatedMarketValue(property) {
  if (!property) return ""
  if (property.purpose === "purchase") {
    return property.salesPrice || property.appraisal
  }
  return property.appraisal
}

/**
 * Remaining credit after closing costs. Positive lender_credit is points cost,
 * not a credit — match Fee Sheet Overview / DetailsV2 getLenderCredit.
 */
export function remainingLenderCredit(product) {
  const closing = Number(product?.closingCost)
  if (Number.isNaN(closing) || closing > 0) return 0
  return -closing
}

export function payoutDisplay(product, purpose) {
  if (purpose === "refinance" || purpose === "ratetermrefinance") {
    return product?.cashToClose
  }
  if (product?.payout == null || product.payout === "") {
    return product?.loanSize
  }
  return product.payout
}

/**
 * Second funds field: purchase payout (loan proceeds); refinance cash-to-close
 * (paydown + closing). Do not label refinance payout as "Paydown principal" —
 * that amount lives on product.paydown and matches Fee Sheet.
 */
export function payoutLabel(purpose) {
  switch (purpose) {
    case "purchase":
      return "Payout"
    case "refinance":
    case "ratetermrefinance":
      return "Cash to close"
    case "cashoutrefinance":
      return "Payout"
    default:
      return "Payout"
  }
}

export function isRefinancePurpose(purpose) {
  return purpose === "refinance"
    || purpose === "ratetermrefinance"
    || purpose === "cashoutrefinance"
}

export function formatMoney(amount, digits = 2) {
  const n = Number(amount)
  if (Number.isNaN(n)) return "$0.00"
  return `$${com.commaizeFloat(n.toFixed(digits))}`
}

export function interestPaid(product, spanYears) {
  if (!product) return 0
  return com.getClosedInterestPaid(
    0.01 * product.baseRate,
    parseInt(product.loanSize, 10) || 0,
    product.term,
    spanYears
  )
}
